import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Breadcrumb,
  Space,
  Button,
  Drawer,
  Statistic,
  Spin,
  Tag,
  Typography,
  Select,
  Input,
  theme,
} from "antd";
import {
  HomeOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  DashboardOutlined,
  DollarCircleOutlined,
  ShoppingOutlined,
  LineChartOutlined,
  SearchOutlined,
  ReloadOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { Line, Column } from "@ant-design/charts";
import { apiRequest } from "../../../../service/apiRequest";
import { showError } from "../../../../components/errorHandler";
import { HPO } from "../../../../types/HPO";

const { Title, Text } = Typography;

// Struct to represent calculated metrics per store supplier
interface StoreSupplierHealth {
  ledger_id: number;
  company_name: string;
  gstin: string;
  total_bills: number;
  total_qty: number;
  total_spend: number;
  avg_bill_value: number;
  avg_unit_rate: number;
  categories_supplied: string[];
  products_supplied: string[];
  rank?: number;
  bills_list: StoreBillWithCalculated[];
}

export interface StoreBillWithCalculated extends HPO {
  calculated_qty: number;
  calculated_amount: number;
  tax_amount: number;
  avg_rate: number;
  product_names: string[];
  category_names: string[];
  items_breakdown: any[];
}

const StorePurchaseSupplierHealth: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [purchaseBills, setPurchaseBills] = useState<HPO[]>([]);
  const [productsList, setProductsList] = useState<any[]>([]);
  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<StoreSupplierHealth | null>(null);
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [drawerCategoryFilter, setDrawerCategoryFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [billsData, prodsRes, catsRes] = await Promise.all([
        apiRequest<HPO[]>({
          method: "get",
          url: "get-po-bill",
        }),
        apiRequest<any>({
          method: "get",
          url: "/get-handicraft-products?limit=1000",
        }).catch(() => ({ data: [] })),
        apiRequest<any[]>({
          method: "get",
          url: "category?status=1",
        }).catch(() => []),
      ]);

      setPurchaseBills(billsData || []);
      const prods = prodsRes?.data || (Array.isArray(prodsRes) ? prodsRes : []);
      setProductsList(prods);
      setCategoriesList(catsRes || []);
    } catch (err) {
      console.error(err);
      showError("Failed to fetch store purchase bills.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Products lookup map
  const productsMap = useMemo(() => {
    const map = new Map<number, any>();
    productsList.forEach((p) => {
      if (p.product_id) {
        map.set(Number(p.product_id), p);
      }
    });
    return map;
  }, [productsList]);

  // Categories lookup map (by id or short code or name)
  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>();
    categoriesList.forEach((c) => {
      if (c.category_short_code) {
        map.set(c.category_short_code.toLowerCase(), c.category_name);
      }
      if (c.category_name) {
        map.set(c.category_name.toLowerCase(), c.category_name);
      }
    });
    return map;
  }, [categoriesList]);

  // Format category name nicely
  const formatCategoryName = (rawType?: string | null): string => {
    if (!rawType) return "Store Items";
    const lower = rawType.trim().toLowerCase();
    if (categoryNameMap.has(lower)) {
      return categoryNameMap.get(lower)!;
    }
    return rawType
      .replace(/[-_]/g, " ")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  // Compute calculated metrics for each bill & group them by supplier (ledger)
  const supplierHealthData = useMemo(() => {
    const suppliersMap = new Map<number, StoreBillWithCalculated[]>();

    purchaseBills.forEach((bill) => {
      // Exclude timber bills (timber has its own Timber Supplier Health report)
      if (bill.pbill_type && String(bill.pbill_type).toLowerCase() === "timber") {
        return;
      }

      let billQty = 0;
      let billAmount = 0;
      let billTax = 0;
      const prodNames = new Set<string>();
      const catNames = new Set<string>();
      const itemsList: any[] = [];

      const billCatName = bill.pbill_type ? formatCategoryName(bill.pbill_type) : null;
      if (billCatName) catNames.add(billCatName);

      if (bill.po_details && bill.po_details.length > 0) {
        bill.po_details.forEach((item: any) => {
          const qty = Number(item.pbg_qty || item.qty || item.quantity || 0);
          const rate = Number(item.pbg_rate || item.rate || item.price || 0);
          const itemAmt = qty * rate;
          const cgst = Number(item.pbg_cgst || 0);
          const sgst = Number(item.pbg_sgst || 0);
          const gst = Number(item.pbg_gst || 0);
          const itemTax = cgst + sgst + gst;

          billQty += qty;
          billAmount += itemAmt;
          billTax += itemTax;

          // Resolve product name and category
          let itemName = item.product_name || item.item_name || "";
          let itemCat = billCatName || "General Store";

          const prodId = Number(item.pbg_description || item.product_id);
          if (productsMap.has(prodId)) {
            const pObj = productsMap.get(prodId);
            if (!itemName && pObj.product_name) itemName = pObj.product_name;
            if (pObj.category_name) {
              itemCat = pObj.category_name;
              catNames.add(pObj.category_name);
            }
          }

          if (!itemName) {
            itemName = `Item #${item.pbg_id || item.pbg_description || "Store Item"}`;
          }

          prodNames.add(itemName);

          itemsList.push({
            id: item.pbg_id || Math.random(),
            product_name: itemName,
            category_name: itemCat,
            qty,
            unit: item.ut_name || item.unit_name || "Units",
            rate,
            tax: itemTax,
            amount: itemAmt,
          });
        });
      }

      // If po_details did not specify an amount, fall back to pbill_grand_total
      const finalAmount = billAmount > 0 ? billAmount : Number(bill.pbill_grand_total || 0);
      const avgRate = billQty > 0 ? finalAmount / billQty : 0;

      const billWithCalculated: StoreBillWithCalculated = {
        ...bill,
        calculated_qty: parseFloat(billQty.toFixed(4)),
        calculated_amount: parseFloat(finalAmount.toFixed(4)),
        tax_amount: parseFloat(billTax.toFixed(4)),
        avg_rate: parseFloat(avgRate.toFixed(4)),
        product_names: Array.from(prodNames).filter(Boolean),
        category_names: Array.from(catNames).filter(Boolean),
        items_breakdown: itemsList,
      };

      const ledgerId = bill.pbill_ledger_id;
      if (!suppliersMap.has(ledgerId)) {
        suppliersMap.set(ledgerId, []);
      }
      suppliersMap.get(ledgerId)!.push(billWithCalculated);
    });

    const suppliersList: StoreSupplierHealth[] = [];

    suppliersMap.forEach((bills, ledgerId) => {
      const firstBill = bills[0];
      const total_bills = bills.length;

      let total_qty = 0;
      let total_spend = 0;
      const allCategories = new Set<string>();
      const allProducts = new Set<string>();

      bills.forEach((b) => {
        total_qty += b.calculated_qty;
        total_spend += b.calculated_amount;
        b.category_names.forEach((c) => allCategories.add(c));
        b.product_names.forEach((p) => allProducts.add(p));
      });

      const avg_bill_value = total_bills > 0 ? total_spend / total_bills : 0;
      const avg_unit_rate = total_qty > 0 ? total_spend / total_qty : 0;

      suppliersList.push({
        ledger_id: ledgerId,
        company_name: firstBill.ledger_company_name || "Unknown Supplier",
        gstin: firstBill.ledger_gstin || "—",
        total_bills,
        total_qty: parseFloat(total_qty.toFixed(4)),
        total_spend: parseFloat(total_spend.toFixed(4)),
        avg_bill_value: parseFloat(avg_bill_value.toFixed(4)),
        avg_unit_rate: parseFloat(avg_unit_rate.toFixed(4)),
        categories_supplied: Array.from(allCategories).filter(Boolean),
        products_supplied: Array.from(allProducts).filter(Boolean),
        bills_list: bills.sort((a, b) => b.pbill_id - a.pbill_id),
      });
    });

    // Ranking algorithm (Highest Spend & Consistent Volume on Top):
    return suppliersList
      .sort((a, b) => {
        if (b.total_spend !== a.total_spend) {
          return b.total_spend - a.total_spend;
        }
        return b.total_bills - a.total_bills;
      })
      .map((supplier, index) => ({
        ...supplier,
        rank: index + 1,
      }));
  }, [purchaseBills, productsMap, categoryNameMap]);

  // Filtered suppliers based on search query and category filter
  const filteredSuppliers = useMemo(() => {
    return supplierHealthData.filter((sup) => {
      const matchSearch =
        !searchQuery.trim() ||
        sup.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sup.gstin.toLowerCase().includes(searchQuery.toLowerCase()) ||
        sup.products_supplied.some((p) => p.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchCategory =
        !categoryFilter ||
        sup.categories_supplied.some(
          (c) => c.toLowerCase() === categoryFilter.toLowerCase()
        );

      return matchSearch && matchCategory;
    });
  }, [supplierHealthData, searchQuery, categoryFilter]);

  // Unique categories across all suppliers for filter dropdown
  const allAvailableCategories = useMemo(() => {
    const set = new Set<string>();
    supplierHealthData.forEach((s) => {
      s.categories_supplied.forEach((c) => set.add(c));
    });
    return Array.from(set).filter(Boolean);
  }, [supplierHealthData]);

  const handleOpenDrawer = (record: StoreSupplierHealth) => {
    setSelectedSupplier(record);
    setDrawerCategoryFilter(null);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setSelectedSupplier(null);
    setDrawerCategoryFilter(null);
  };

  // Available categories / products for chosen supplier inside Drawer
  const drawerFilterOptions = useMemo(() => {
    if (!selectedSupplier) return [];
    const set = new Set<string>();
    selectedSupplier.categories_supplied.forEach((c) => set.add(c));
    selectedSupplier.products_supplied.forEach((p) => set.add(p));
    return Array.from(set).filter(Boolean);
  }, [selectedSupplier]);

  // Filtered bills in Drawer based on drawerCategoryFilter
  const displayedBillsList = useMemo(() => {
    if (!selectedSupplier) return [];
    if (!drawerCategoryFilter) return selectedSupplier.bills_list;

    const filterVal = drawerCategoryFilter.toLowerCase();
    return selectedSupplier.bills_list.filter((b) => {
      const catMatch = b.category_names.some((c) => c.toLowerCase() === filterVal);
      const prodMatch = b.product_names.some((p) => p.toLowerCase() === filterVal);
      return catMatch || prodMatch;
    });
  }, [selectedSupplier, drawerCategoryFilter]);

  // Metrics calculated dynamically for filtered bills in Drawer
  const drawerStats = useMemo(() => {
    if (!selectedSupplier) return null;
    const bills = displayedBillsList;
    const total_bills = bills.length;
    let total_qty = 0;
    let total_spend = 0;
    const itemsSet = new Set<string>();

    bills.forEach((b) => {
      total_qty += b.calculated_qty;
      total_spend += b.calculated_amount;
      b.product_names.forEach((p) => itemsSet.add(p));
    });

    const avg_bill_value = total_bills > 0 ? total_spend / total_bills : 0;

    return {
      total_bills,
      total_qty,
      total_spend,
      avg_bill_value,
      unique_items: itemsSet.size,
    };
  }, [selectedSupplier, displayedBillsList]);

  // Drawer charts data
  const spendTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        amount: b.calculated_amount,
      }))
      .reverse();
  }, [displayedBillsList]);

  const qtyTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        qty: b.calculated_qty,
      }))
      .reverse();
  }, [displayedBillsList]);

  const priceTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        rate: b.avg_rate,
      }))
      .reverse();
  }, [displayedBillsList]);

  // Columns for main table
  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 80,
      align: "center" as const,
      render: (rank: number) => {
        let color = "blue";
        if (rank === 1) color = "gold";
        else if (rank === 2) color = "cyan";
        else if (rank === 3) color = "purple";
        return (
          <Tag color={color} style={{ fontWeight: "bold", fontSize: 13, borderRadius: 6 }}>
            Top {rank}
          </Tag>
        );
      },
    },
    {
      title: "Supplier / Company Name",
      key: "company_name",
      render: (_: any, record: StoreSupplierHealth) => (
        <div>
          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15 }}>
            {record.company_name}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            GSTIN: <strong>{record.gstin}</strong>
          </div>
        </div>
      ),
    },
    {
      title: "Categories Supplied",
      key: "categories",
      render: (_: any, record: StoreSupplierHealth) => {
        if (!record.categories_supplied || record.categories_supplied.length === 0) {
          return <span style={{ color: "#94a3b8" }}>Store Items</span>;
        }
        return (
          <Space size={[4, 4]} wrap>
            {record.categories_supplied.map((cat) => (
              <Tag color="cyan" key={cat} style={{ borderRadius: 4, fontWeight: 500 }}>
                {cat}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Invoices",
      dataIndex: "total_bills",
      key: "total_bills",
      width: 100,
      align: "center" as const,
      sorter: (a: StoreSupplierHealth, b: StoreSupplierHealth) => a.total_bills - b.total_bills,
      render: (val: number) => <span style={{ fontWeight: 700 }}>{val}</span>,
    },
    {
      title: "Total Qty",
      dataIndex: "total_qty",
      key: "total_qty",
      width: 130,
      align: "right" as const,
      sorter: (a: StoreSupplierHealth, b: StoreSupplierHealth) => a.total_qty - b.total_qty,
      render: (qty: number) => (
        <span style={{ fontWeight: 600, color: "#0f172a" }}>
          {qty.toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Total Spend (₹)",
      dataIndex: "total_spend",
      key: "total_spend",
      width: 160,
      align: "right" as const,
      sorter: (a: StoreSupplierHealth, b: StoreSupplierHealth) => a.total_spend - b.total_spend,
      render: (spend: number) => (
        <span style={{ color: "#52c41a", fontWeight: 800, fontSize: 15 }}>
          ₹ {spend.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Avg Invoice Spend",
      dataIndex: "avg_bill_value",
      key: "avg_bill_value",
      width: 150,
      align: "right" as const,
      sorter: (a: StoreSupplierHealth, b: StoreSupplierHealth) => a.avg_bill_value - b.avg_bill_value,
      render: (val: number) => (
        <span style={{ fontWeight: 600, color: "#1677ff" }}>
          ₹ {val.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center" as const,
      render: (_: any, record: StoreSupplierHealth) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="middle"
          onClick={() => handleOpenDrawer(record)}
          style={{ borderRadius: 6, fontWeight: 600 }}
        >
          View
        </Button>
      ),
    },
  ];

  // Aggregated totals for top statistic cards
  const totalAuditedSuppliers = supplierHealthData.length;
  const overallTotalSpend = supplierHealthData.reduce((sum, s) => sum + s.total_spend, 0);
  const overallTotalQty = supplierHealthData.reduce((sum, s) => sum + s.total_qty, 0);
  const overallTotalBills = supplierHealthData.reduce((sum, s) => sum + s.total_bills, 0);

  return (
    <div style={{ padding: "24px 20px", minHeight: "90vh", backgroundColor: "#f8fafc" }}>
      {/* Breadcrumb Header */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/reports-menu">Reports</Breadcrumb.Item>
        <Breadcrumb.Item>Supplier Health Card (Store Purchase)</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header Panel */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: "#0f172a", letterSpacing: "-0.5px" }}>
            Store Purchase Supplier Health Card
          </h1>
          <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 14 }}>
            Aggregated supplier performance, purchase volume, item categories, price trends, and invoice logs for store materials (Hardware, Finishing, Packing, Consumables, Ply & MDF).
          </p>
        </div>
      </div>

      {/* Quick Overview Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Suppliers Audited</span>}
              value={totalAuditedSuppliers}
              prefix={<SafetyCertificateOutlined style={{ color: "#1677ff", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#0f172a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Total Store Spend</span>}
              value={overallTotalSpend}
              precision={2}
              prefix={<DollarCircleOutlined style={{ color: "#52c41a", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#52c41a" }}
              formatter={(val) => `₹ ${Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Total Units Supplied</span>}
              value={overallTotalQty}
              precision={0}
              prefix={<ShoppingOutlined style={{ color: "#8b5cf6", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#8b5cf6" }}
              formatter={(val) => `${Number(val).toLocaleString("en-IN")} Units`}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Total Invoices</span>}
              value={overallTotalBills}
              prefix={<DashboardOutlined style={{ color: "#f59e0b", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#f59e0b" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter / Search Bar */}
      <Card bordered={false} style={{ borderRadius: 12, marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} md={12} lg={10}>
            <Input
              size="large"
              placeholder="Search supplier by name, GSTIN, or product supplied..."
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              size="large"
              style={{ width: "100%" }}
              placeholder="Filter by Store Category"
              allowClear
              value={categoryFilter}
              onChange={(val) => setCategoryFilter(val || null)}
              options={allAvailableCategories.map((cat) => ({ label: cat, value: cat }))}
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={4}>
            <Button
              size="large"
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearchQuery("");
                setCategoryFilter(null);
                fetchAllData();
              }}
              style={{ borderRadius: 8 }}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Main Health Table */}
      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG, padding: 20, boxShadow: "0 4px 18px rgba(0,0,0,0.02)" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table<StoreSupplierHealth>
            columns={columns}
            dataSource={filteredSuppliers}
            rowKey="ledger_id"
            bordered
            size="middle"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} store suppliers`,
            }}
            locale={{ emptyText: "No store purchase suppliers found" }}
          />
        )}
      </div>

      {/* Detail Drawer */}
      <Drawer
        title={
          selectedSupplier ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
                  Supplier Health: {selectedSupplier.company_name}
                </Title>
                <Tag color="gold" style={{ fontWeight: 700 }}>
                  Rank #{selectedSupplier.rank}
                </Tag>
              </div>
              <Text type="secondary" style={{ fontSize: 13 }}>
                GSTIN: {selectedSupplier.gstin}
              </Text>
            </div>
          ) : (
            "Supplier Detail"
          )
        }
        width={1000}
        onClose={handleCloseDrawer}
        open={drawerVisible}
        destroyOnClose
        style={{ background: "#f8fafc" }}
      >
        {selectedSupplier && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Top Category / Product Filter Bar */}
            <Card
              size="small"
              bordered={false}
              style={{
                borderRadius: 10,
                background: "#ffffff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <Space align="center" size="middle">
                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>
                    <ShopOutlined style={{ marginRight: 6, color: "#1677ff" }} /> Filter by Category / Item:
                  </span>
                  <Select
                    allowClear
                    showSearch
                    placeholder="All Store Categories & Items"
                    style={{ width: 320 }}
                    value={drawerCategoryFilter}
                    onChange={(val) => setDrawerCategoryFilter(val || null)}
                    options={drawerFilterOptions.map((name) => ({ label: name, value: name }))}
                  />
                </Space>

                {drawerCategoryFilter ? (
                  <Tag
                    color="blue"
                    closable
                    onClose={() => setDrawerCategoryFilter(null)}
                    style={{ fontSize: 13, padding: "3px 10px", borderRadius: 6 }}
                  >
                    Active Filter: <strong>{drawerCategoryFilter}</strong>
                  </Tag>
                ) : (
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    Showing all {selectedSupplier.total_bills} invoices
                  </span>
                )}
              </div>
            </Card>

            {/* Drawer Stat Row */}
            <Row gutter={16}>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Invoices Count"
                    value={drawerStats?.total_bills ?? selectedSupplier.total_bills}
                    suffix="bills"
                    valueStyle={{ fontWeight: 700, fontSize: 18, color: "#0f172a" }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Total Units Supplied"
                    value={drawerStats?.total_qty ?? selectedSupplier.total_qty}
                    precision={0}
                    suffix="units"
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#8b5cf6",
                    }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Total Spend"
                    value={drawerStats?.total_spend ?? selectedSupplier.total_spend}
                    precision={2}
                    prefix="₹ "
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#52c41a",
                    }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Avg Invoice Spend"
                    value={drawerStats?.avg_bill_value ?? selectedSupplier.avg_bill_value}
                    precision={2}
                    prefix="₹ "
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#1677ff",
                    }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Performance Charts */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
                <LineChartOutlined style={{ marginRight: 8 }} /> Performance & Trend Analysis {drawerCategoryFilter && `(${drawerCategoryFilter})`}
              </Title>
              {drawerCategoryFilter && (
                <span style={{ fontSize: 12, color: "#1677ff", fontWeight: 600 }}>
                  Showing trends specifically for {drawerCategoryFilter}
                </span>
              )}
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <Card title="Invoice Spend Trend (₹)" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {spendTrendData.length > 0 ? (
                    <Column
                      data={spendTrendData}
                      xField="bill_no"
                      yField="amount"
                      height={180}
                      color="#52c41a"
                      tooltip={{ fields: ["bill_no", "amount"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No spend history available.</div>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="Quantity Supplied Trend" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {qtyTrendData.length > 0 ? (
                    <Column
                      data={qtyTrendData}
                      xField="bill_no"
                      yField="qty"
                      height={180}
                      color="#8b5cf6"
                      tooltip={{ fields: ["bill_no", "qty"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No quantity history available.</div>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="Unit Price Trend (₹)" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {priceTrendData.length > 0 ? (
                    <Line
                      data={priceTrendData}
                      xField="bill_no"
                      yField="rate"
                      height={180}
                      smooth
                      color="#1677ff"
                      tooltip={{ fields: ["bill_no", "rate"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No price history available.</div>
                  )}
                </Card>
              </Col>
            </Row>

            {/* Individual Purchase Bills Detail Table */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "8px 0 12px 0",
                }}
              >
                <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
                  📋 Individual Store Purchase Invoices {drawerCategoryFilter && `(${drawerCategoryFilter})`}
                </Title>
                <span style={{ fontSize: 12, color: "#64748b" }}>
                  Showing <strong>{displayedBillsList.length}</strong> {displayedBillsList.length === 1 ? "invoice" : "invoices"}
                </span>
              </div>

              <Table<StoreBillWithCalculated>
                dataSource={displayedBillsList}
                size="small"
                bordered
                pagination={{ defaultPageSize: 5 }}
                rowKey="pbill_id"
                expandable={{
                  expandedRowRender: (record) => (
                    <div style={{ background: "#f1f5f9", padding: "12px 16px", borderRadius: 8 }}>
                      <div style={{ fontWeight: 700, marginBottom: 8, color: "#334155" }}>
                        📦 Line Items Breakdown ({record.items_breakdown.length} items):
                      </div>
                      <Table
                        dataSource={record.items_breakdown}
                        rowKey="id"
                        size="small"
                        pagination={false}
                        bordered
                        columns={[
                          {
                            title: "Item / Product Name",
                            dataIndex: "product_name",
                            key: "product_name",
                            render: (name: string) => <strong>{name}</strong>,
                          },
                          {
                            title: "Category",
                            dataIndex: "category_name",
                            key: "category_name",
                            render: (cat: string) => <Tag color="blue">{cat}</Tag>,
                          },
                          {
                            title: "Quantity",
                            dataIndex: "qty",
                            key: "qty",
                            align: "right" as const,
                            render: (qty: number, it: any) => `${qty} ${it.unit}`,
                          },
                          {
                            title: "Rate (₹)",
                            dataIndex: "rate",
                            key: "rate",
                            align: "right" as const,
                            render: (rate: number) => `₹ ${rate.toFixed(4)}`,
                          },
                          {
                            title: "GST / Tax (₹)",
                            dataIndex: "tax",
                            key: "tax",
                            align: "right" as const,
                            render: (tax: number) => `₹ ${tax.toFixed(4)}`,
                          },
                          {
                            title: "Total Amount (₹)",
                            dataIndex: "amount",
                            key: "amount",
                            align: "right" as const,
                            render: (amt: number) => (
                              <strong style={{ color: "#52c41a" }}>
                                ₹ {amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </strong>
                            ),
                          },
                        ]}
                      />
                    </div>
                  ),
                  rowExpandable: (record) => record.items_breakdown && record.items_breakdown.length > 0,
                }}
                columns={[
                  {
                    title: "Bill No.",
                    dataIndex: "pbill_bill_no",
                    key: "pbill_bill_no",
                    render: (val: any, record: StoreBillWithCalculated) => (
                      <div>
                        <strong>{val || `ID-${record.pbill_id}`}</strong>
                        {record.pbill_challan_no && (
                          <div style={{ fontSize: 10, color: "#94a3b8" }}>Challan: {record.pbill_challan_no}</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: "Bill Date",
                    dataIndex: "pbill_bill_date",
                    key: "pbill_bill_date",
                  },
                  {
                    title: "Categories",
                    key: "categories",
                    render: (_: any, record: StoreBillWithCalculated) => (
                      <Space size={[2, 4]} wrap>
                        {record.category_names.map((c) => (
                          <Tag color="cyan" key={c}>
                            {c}
                          </Tag>
                        ))}
                      </Space>
                    ),
                  },
                  {
                    title: "Products Supplied",
                    key: "product_names",
                    render: (_: any, record: StoreBillWithCalculated) => (
                      <Space size={[2, 4]} wrap>
                        {record.product_names.slice(0, 3).map((p) => (
                          <Tag key={p} style={{ borderRadius: 4 }}>
                            {p}
                          </Tag>
                        ))}
                        {record.product_names.length > 3 && (
                          <Tag color="default">+{record.product_names.length - 3} more</Tag>
                        )}
                      </Space>
                    ),
                  },
                  {
                    title: "Total Qty",
                    dataIndex: "calculated_qty",
                    key: "calculated_qty",
                    align: "right" as const,
                    render: (qty: number) => <strong>{qty.toLocaleString("en-IN")}</strong>,
                  },
                  {
                    title: "Tax Amount",
                    dataIndex: "tax_amount",
                    key: "tax_amount",
                    align: "right" as const,
                    render: (tax: number) => `₹ ${tax.toFixed(4)}`,
                  },
                  {
                    title: "Bill Amount (₹)",
                    dataIndex: "calculated_amount",
                    key: "calculated_amount",
                    align: "right" as const,
                    render: (amt: number) => (
                      <span style={{ fontWeight: 700, color: "#52c41a" }}>
                        ₹ {amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default StorePurchaseSupplierHealth;
